import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Dropdown } from "react-bootstrap";
import { useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import ShowHideColumnsModal from "../../../components/modals/showHideColumns";
import OverlayLoader from "../../../components/overlayLoader";
import { SkeletonRows } from "../../../components/skeleton";
import Pages from "../../../components/pages";
import { getJobsOverallTotal, getPaginatedJobReports } from "../../../services/jobs";
import { hasValue, rsdFormat, toLocalDateString } from "../../../utils/helper";
import Avatar from "../../../components/avatar";
import { onQuerySuccess } from "../../../utils/queryResponse";
import { msToHHMM } from "../../../utils/timeHelpers";
import JobReportsFilter from "./jobReportsFilter";
import SetTempEditOrderModal from "../../../components/modals/orderEditTemp";
import { useSearchParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { getStatusLabel } from "../../../constants/jobStatus";

// Labels are i18n keys, translated inside the component (see `columns`) so
// they follow the active language instead of freezing at import time.
const COLUMN_DEFS = [
    { value: 'id', labelKey: 'reports.jobNumber' },
    { value: 'client', labelKey: 'reports.clientName' },
    { value: 'jobStart', labelKey: 'reports.colJobStart' },
    { value: 'status', labelKey: 'jobLabel.status' },
    { value: 'lineItems', labelKey: 'reports.colTags' },
    { value: 'assignees', labelKey: 'reports.assignees' },
    { value: 'paymentStatus', labelKey: 'reports.paymentStatusFilter' },
    { value: 'price', labelKey: 'jobLabel.price' },
    { value: 'onTime', labelKey: 'reports.colOnTime' },
    { value: 'actualDuration', labelKey: 'reports.colActualDuration' },
    { value: 'cleaningCompletedTime', labelKey: 'reports.colCleaningCompletedTime' },
    { value: 'completionOnTime', labelKey: 'reports.colCompletionOnTime' },
    { value: 'completionTime', labelKey: 'jobLabel.completionTime' },
    { value: 'completionTimeActual', labelKey: 'reports.colCompletionTimeActual' },
    { value: 'pickupTime', labelKey: 'jobLabel.pickupTime' },
    { value: 'deliveredByCustomerTime', labelKey: 'reports.colDeliveredByCustomerTime' },
    { value: 'pickupInProgressTime', labelKey: 'reports.colPickupInProgressTime' },
    { value: 'pickupCompletedTime', labelKey: 'reports.colPickupCompletedTime' },
    { value: 'cleaningTime', labelKey: 'reports.colCleaningTime' },
    { value: 'dispatchTime', labelKey: 'jobLabel.dispatchTime' },
    { value: 'dispatchInProgressTime', labelKey: 'reports.colDispatchInProgressTime' },
    { value: 'expectedDuration', labelKey: 'reports.colExpectedDuration' },
    { value: 'note', labelKey: 'reports.colNote' },
    { value: 'paymentMethod', labelKey: 'jobLabel.paymentMethod' },
    { value: 'paymentTime', labelKey: 'jobLabel.paymentTime' },
    { value: 'paymentType', labelKey: 'jobLabel.paymentType' },
    { value: 'pickupType', labelKey: 'jobLabel.pickupType' },
];

const pageStyles = `
  .sbr .hist-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px}
  @media(max-width:1100px){.sbr .hist-kpis{grid-template-columns:1fr 1fr}}
  .sbr .ktile{background:var(--card);border:1px solid var(--line);border-radius:var(--r-md);padding:15px 16px;
    box-shadow:var(--sh-2);position:relative;overflow:hidden}
  .sbr .ktile::after{content:"";position:absolute;inset:0 0 auto 0;height:3px;background:var(--accent,var(--brand))}
  .sbr .ktile.a-done{--accent:var(--done)} .sbr .ktile.a-cancel{--accent:var(--bad)}
  .sbr .ktile.a-brand{--accent:var(--brand)} .sbr .ktile.a-warn{--accent:var(--warn)}
  .sbr .ktile .kc{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:800;letter-spacing:.5px;
    text-transform:uppercase;color:var(--muted)}
  .sbr .ktile .kic{width:15px;height:15px;color:var(--accent,var(--brand))}
  .sbr .ktile .kn{font-size:22px;font-weight:800;letter-spacing:-.7px;color:var(--ink);margin-top:9px;
    font-variant-numeric:tabular-nums;line-height:1}
  .sbr .ktile .ks{font-size:12px;color:var(--muted);font-weight:600;margin-top:5px}
  .sbr .toolbar{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:14px}
  .sbr .h-search{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid var(--line);
    border-radius:10px;padding:9px 13px;color:#94A3B8;font-size:13px;font-weight:500;box-shadow:var(--sh-1)}
  .sbr .h-search input{border:0;outline:0;background:transparent;font-size:13px;font-weight:500;color:var(--ink);min-width:160px}
  .sbr .h-search input::placeholder{color:#94A3B8}
  .sbr .h-search .ic{width:15px;height:15px;flex-shrink:0}
  .sbr .tpanel{background:var(--card);border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-2);overflow:hidden}
  .sbr .tpanel .tbl{font-size:13px;width:100%;border-collapse:collapse}
  .sbr .tpanel .tbl thead th{padding:14px 16px 12px;background:#FBFCFE;font-weight:700;color:var(--muted);font-size:12px;text-align:left;border-bottom:1px solid var(--line)}
  .sbr .tpanel .tbl thead th.r{text-align:right}
  .sbr .tpanel .tbl tbody td{padding:13px 16px;border-bottom:1px solid var(--line-soft)}
  .sbr .tpanel .tbl tbody tr:last-child td{border-bottom:0}
  .sbr .tpanel .tbl tbody tr:hover td{background:var(--brand-50)}
  .sbr .tpanel .tbl tbody tr.cancelled td{color:var(--faint)}
  .sbr .tpanel .tbl tbody tr.cancelled .cli{color:var(--muted)}
  .sbr .tpanel .tbl tbody tr.cancelled .money{color:var(--muted);text-decoration:line-through;text-decoration-color:var(--bad)}
  .sbr .jno{font-variant-numeric:tabular-nums;font-weight:800;color:var(--brand-ink);font-size:12.5px;cursor:pointer}
  .sbr .cli{font-weight:700;color:var(--ink);font-size:13px}
  .sbr .dt{color:var(--muted);font-weight:600;font-variant-numeric:tabular-nums;font-size:12.5px}
  .sbr .dt small{display:block;font-size:11px;color:var(--faint);font-weight:600;margin-top:1px}
  .sbr .asg{display:flex;align-items:center;gap:8px}
  .sbr .asg .av{width:26px;height:26px;border-radius:8px;flex-shrink:0;background:var(--brand-50);
    border:1px solid var(--brand-100);color:var(--brand-ink);font-size:10px;font-weight:800;
    display:flex;align-items:center;justify-content:center}
  .sbr .asg .anm{font-size:12.5px;font-weight:600;color:var(--text);white-space:nowrap}
  .sbr .money{font-variant-numeric:tabular-nums;font-weight:700;color:var(--ink);text-align:right}
  .sbr .b-completed{color:var(--done);background:var(--done-bg);border-radius:6px;padding:3px 8px;font-size:11.5px;font-weight:700;white-space:nowrap}
  .sbr .b-cancelled{color:var(--bad);background:var(--bad-bg);border-radius:6px;padding:3px 8px;font-size:11.5px;font-weight:700;white-space:nowrap}
  .sbr .tfoot{display:flex;align-items:center;gap:14px;padding:13px 16px;border-top:1px solid var(--line);background:#FBFCFE}
  .sbr .tfoot .rs{font-size:12.5px;color:var(--muted);font-weight:600}
  .sbr .tfoot .rs b{color:var(--ink);font-weight:800}
  /* horizontal scroll wrapper so the wide table can be swiped on mobile instead of being clipped */
  .sbr .tpanel .tscroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
  .sbr .tpanel .tscroll::-webkit-scrollbar{height:6px}
  .sbr .tpanel .tscroll::-webkit-scrollbar-thumb{background:var(--line);border-radius:999px}
  .sbr .tpanel .tscroll::-webkit-scrollbar-track{background:transparent}
  @media(max-width:899px){.sbr .tpanel .tbl{min-width:760px}}

  /* ---------- card / row list (replaces the dense spreadsheet table) ---------- */
  .sbr .hlist{display:flex;flex-direction:column;gap:10px}
  .sbr .hjob{position:relative;display:grid;align-items:center;gap:14px;
    grid-template-columns:74px minmax(0,1.6fr) 116px minmax(0,1fr) 118px 130px;
    background:#fff;border:1px solid var(--line);border-radius:14px;padding:14px 18px 14px 20px;
    box-shadow:var(--sh-2);cursor:pointer;transition:box-shadow .15s,border-color .15s,transform .08s}
  .sbr .hjob:hover{box-shadow:0 8px 22px rgba(16,24,40,.10);border-color:var(--brand-100)}
  .sbr .hfoot{display:flex;align-items:center;gap:14px;padding:13px 16px;margin-top:12px;
    background:var(--card);border:1px solid var(--line);border-radius:12px;box-shadow:var(--sh-1)}
  .sbr .hfoot .rs{font-size:12.5px;color:var(--muted);font-weight:600}
  .sbr .hfoot .rs b{color:var(--ink);font-weight:800}
  .sbr .hjob:active{transform:scale(.997)}
  .sbr .hjob::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;border-radius:14px 0 0 14px;background:var(--accent,var(--line))}
  .sbr .hjob.s-done{--accent:var(--done)} .sbr .hjob.s-cancel{--accent:var(--bad)}
  .sbr .hjob.s-active{--accent:var(--brand)} .sbr .hjob.s-pending{--accent:var(--warn)}
  .sbr .hjob.cancelled .hcli,.sbr .hjob.cancelled .hsvc{color:var(--muted)}
  .sbr .hjob.cancelled .hamt{color:var(--muted);text-decoration:line-through;text-decoration-color:var(--bad)}
  .sbr .hjno{font-variant-numeric:tabular-nums;font-weight:800;color:var(--brand-ink);font-size:13px}
  .sbr .hcli{font-weight:700;color:var(--ink);font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sbr .hsvc{font-size:12px;color:var(--muted);font-weight:600;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sbr .hdt{color:var(--muted);font-weight:600;font-variant-numeric:tabular-nums;font-size:12.5px}
  .sbr .hdt small{display:block;font-size:11px;color:var(--faint);font-weight:600;margin-top:1px}
  .sbr .hasg{display:flex;align-items:center;gap:8px;min-width:0}
  .sbr .hasg .av{width:26px;height:26px;border-radius:8px;flex-shrink:0;background:var(--brand-50);
    border:1px solid var(--brand-100);color:var(--brand-ink);font-size:10px;font-weight:800;
    display:flex;align-items:center;justify-content:center}
  .sbr .hasg .anm{font-size:12.5px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sbr .hright{display:flex;flex-direction:column;align-items:flex-end;gap:5px}
  .sbr .hamt{font-variant-numeric:tabular-nums;font-weight:800;color:var(--ink);font-size:13.5px}
  .sbr .hstat{display:flex;justify-content:flex-start}
  /* mobile: collapse the grid into a stacked card */
  @media(max-width:899px){
    .sbr .hjob{grid-template-columns:1fr auto;
      grid-template-areas:"id amt" "cli amt" "meta stat" "asg stat";gap:6px 12px}
    .sbr .hjob .hcid{grid-area:id} .sbr .hjob .hccli{grid-area:cli}
    .sbr .hjob .hcmeta{grid-area:meta} .sbr .hjob .hcasg{grid-area:asg}
    .sbr .hjob .hcstat{grid-area:stat;align-self:center} .sbr .hjob .hcamt{grid-area:amt;align-self:start}
    .sbr .hright{flex-direction:row;align-items:center}
  }
`;

const JobReportsPage = () => {

    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    const [showHideColumnsModal, setShowHideColumnsModal] = useState(false)
    const [editJobModal, setEditJobModal] = useState(false)

    const [reports, setReports] = useState([])
    const [selectedJobId, setSelectedJobId] = useState('')
    const [overallTotal, setOverallTotal] = useState()
    const [searchQuery, setSearchQuery] = useState('')

    const [shownColumns, setShownColumns] = useState([])
    const [pagination, setPagination] = useState()
    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [filters, setFilters] = useState([
        '', '', '', '', '', '',
        searchParams.get("pickupAssignedToId"),
        searchParams.get("cleaningAssignedToId"),
        searchParams.get("deliveryAssignedToId"),
        searchParams.get("serviceId"),
        '', '', '', '', ''
    ])

    const printRef = useRef()

    const columns = useMemo(
        () => COLUMN_DEFS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
        [t]
    )
    // Saved selections (localStorage) may carry labels in another language —
    // always show the current translation for each chosen column.
    const labelledShownColumns = useMemo(
        () => shownColumns.map(col => columns.find(c => c.value === (col.value || col)) || col),
        [shownColumns, columns]
    )

    const { data, isFetching } = useQuery(
        ['job-reports', pageNumber, pageSize, ...filters],
        () => getPaginatedJobReports(pageNumber, pageSize, ...filters)
    )

    const { data: jobsOverallTotalData } = useQuery(['jobs-overall-total'], () => getJobsOverallTotal())

    const storageName = 'crm-jobreports-cols'

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result)
            setReports(result.data.map(x => {
                x.price = parseFloat(x.price)
                const { price, ...rest } = x;
                return { price, ...rest };
            }))
        })

        setShownColumns(
            JSON.parse(localStorage.getItem(storageName)) ||
            COLUMN_DEFS.slice(0, 9).map(col => col.value)
        )

        printRef?.current?.scrollIntoView()
    }, [data])

    useEffect(() => {
        onQuerySuccess(jobsOverallTotalData, (result) => {
            setOverallTotal(result)
        })
    }, [jobsOverallTotalData])

    const handleSetPageSize = (size) => {
        setPageNumber(1)
        setPageSize(Number(size))
    }

    // Reset to page 1 whenever the filters change — otherwise a filter that
    // returns fewer pages leaves us on an out-of-range page that renders empty
    // until a manual refresh ("certain jobs don't load" bug).
    const handleFilter = (nextFilters) => {
        setPageNumber(1)
        setFilters(nextFilters)
    }

    // Quick client-side search over the loaded page (job #, client, badge #).
    const q = searchQuery.trim().toLowerCase()
    const visibleReports = q
        ? reports.filter(job => {
            const badges = Array.isArray(job.lineItems)
                ? job.lineItems.map(li => li?.badge?.badgeNo).filter(v => v != null).join(' ')
                : ''
            return [
                job.id != null ? String(job.id) : '',
                job.client?.firstName,
                job.client?.lastName,
                badges,
            ].filter(Boolean).join(' ').toLowerCase().includes(q)
        })
        : reports

    const handleEditJob = (job) => {
        setSelectedJobId(job.id)
        setEditJobModal(true)
    }

    const getJobStart = (data) => {
        if (data.pickupType === "PICKUP_FROM_HOME")
            return toLocalDateString(data.pickupTime)
        else
            return toLocalDateString(data.deliveredByCustomerTime)
    }

    return (
        <div className="sbr">
            <style>{pageStyles}</style>
            {columns && shownColumns &&
                <ShowHideColumnsModal display={showHideColumnsModal} toggle={setShowHideColumnsModal}
                    columns={columns} shownColumns={labelledShownColumns} setShownColumns={setShownColumns} storageName={storageName}
                />
            }
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />

            {/* Desktop header */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('reports.jobsHistoryTitle')}</div>
                    <div className="sub">{t('reports.reportsClosedJobs')} {pagination ? `· ${pagination.totalItems} ${t('reports.totalLabel')}` : ''}</div>
                </div>
                <div className="act">
                    <Dropdown>
                        <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="jobreports-export" disabled={isFetching}>
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            {t('reports.exportCsv')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item as="button">
                                <ReactToPrint trigger={() => <span>{t('sales.print')}</span>} content={() => printRef.current} />
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setShowHideColumnsModal(true)}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                        {t('reports.columns')}
                    </button>
                </div>
            </div>

            {/* Mobile header */}
            <div className="sbr-m" style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.8px', color: 'var(--ink)' }}>{t('reports.jobsHistoryTitle')}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>{t('reports.reportsClosedJobs')}</div>
            </div>

            {/* KPI tiles (desktop only) */}
            <div className="hist-kpis sbr-d">
                <div className="ktile a-brand">
                    <div className="kc">
                        <svg className="kic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                        {t('reports.jobsClosed')}
                    </div>
                    <div className="kn num">{pagination?.totalItems ?? '—'}</div>
                    <div className="ks">{t('reports.inSelectedRange')}</div>
                </div>
                <div className="ktile a-done">
                    <div className="kc">
                        <svg className="kic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        {t('reports.completedLabel')}
                    </div>
                    <div className="kn num">{reports.filter(r => r.status === 'COMPLETED').length || (pagination ? pagination.totalItems : '—')}</div>
                    <div className="ks">{t('reports.matchingCurrentFilters')}</div>
                </div>
                <div className="ktile a-cancel">
                    <div className="kc">
                        <svg className="kic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                        {t('reports.cancelledLabel')}
                    </div>
                    <div className="kn num">{reports.filter(r => r.status === 'CANCELLED').length || 0}</div>
                    <div className="ks">{t('reports.ofClosedJobs')}</div>
                </div>
                <div className="ktile a-warn">
                    <div className="kc">
                        <svg className="kic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        {t('reports.revenueBilled')}
                    </div>
                    <div className="kn num">{overallTotal ? rsdFormat(overallTotal) : '—'}</div>
                    <div className="ks">{t('reports.overallTotal')}</div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
                <div className="h-search">
                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input type="text" placeholder={t('reports.searchByJobOrClient')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select style={{ fontSize: '12.5px', fontWeight: 600, border: '1px solid var(--line)', borderRadius: '8px', padding: '7px 9px', background: '#fff', color: 'var(--text)', cursor: 'pointer' }} onChange={(e) => handleSetPageSize(e.target.value)} defaultValue="10">
                        <option value="10">{t('reports.nPerPage', { n: 10 })}</option>
                        <option value="20">{t('reports.nPerPage', { n: 20 })}</option>
                        <option value="50">{t('reports.nPerPage', { n: 50 })}</option>
                        <option value="100">{t('reports.nPerPage', { n: 100 })}</option>
                    </select>
                </div>
            </div>

            {/* Filter component */}
            <div style={{ marginBottom: 14 }}>
                <JobReportsFilter onFilter={handleFilter} isFiltering={isFetching} />
            </div>

            {/* Card / row list */}
            <div className="hwrap" ref={printRef} style={{ position: 'relative' }}>
                {isFetching && reports.length === 0 ? (
                    <div className="tpanel" style={{ padding: '16px 20px' }}>
                        <SkeletonRows rows={8} widths={['8%','14%','22%','14%','14%','10%','10%','8%']} />
                    </div>
                ) : visibleReports.length === 0 ? (
                    <div className="tpanel sbr-empty" style={{ padding: 40 }}>
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        {q ? t('reports.noJobsMatchSearch') : t('reports.noReportsFound')}
                    </div>
                ) : (
                    <div className="hlist">
                        {visibleReports.map((job) => {
                            const isCancelled = job.status === 'CANCELLED'
                            const isCompleted = job.status === 'COMPLETED'
                            const accentCls = isCancelled ? 's-cancel' : isCompleted ? 's-done' : 's-active'
                            const assignee = job.cleaningAssignedTo || job.pickupAssignedTo || job.deliveryAssignedTo
                            const jobDate = getJobStart(job)
                            const dateParts = jobDate ? jobDate.split(' ') : []
                            const firstItem = (job.lineItems || [])[0]
                            const svcName = (firstItem?.type === 'MATERIAL' ? firstItem.material?.name : firstItem?.service?.name) || job.type || '—'
                            const area = job.area || job.address || ''
                            return (
                                <div key={job.id} className={`hjob ${accentCls}${isCancelled ? ' cancelled' : ''}`} onClick={() => handleEditJob(job)}>
                                    <div className="hcid">
                                        <span className="hjno">#{job.id}</span>
                                    </div>
                                    <div className="hccli">
                                        <div className="hcli">{job.client?.firstName} {job.client?.lastName}</div>
                                        <div className="hsvc">{svcName}{area ? ` · ${area.split(',')[0]}` : ''}</div>
                                    </div>
                                    <div className="hcmeta">
                                        <span className="hdt">
                                            {dateParts.length ? `${dateParts[0]} ${dateParts[1] || ''} ${dateParts[2] || ''}`.trim() : '—'}
                                            <small>{dateParts.length > 3 ? dateParts.slice(3).join(' ') : ''}</small>
                                        </span>
                                    </div>
                                    <div className="hcasg">
                                        {assignee ? (
                                            <div className="hasg">
                                                <Avatar className="av" size={26} name={assignee.fullName} src={assignee?.avatarUrl} />
                                                <span className="anm">{assignee.fullName}</span>
                                            </div>
                                        ) : <span style={{ color: 'var(--faint)', fontSize: 12 }}>—</span>}
                                    </div>
                                    <div className="hcstat hstat">
                                        {isCancelled
                                            ? <span className="b-cancelled">{t('reports.cancelledLabel')}</span>
                                            : isCompleted
                                            ? <span className="b-completed">{t('reports.completedLabel')}</span>
                                            : <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{job.status ? getStatusLabel(job.status) : ''}</span>
                                        }
                                    </div>
                                    <div className="hcamt hright">
                                        <span className="hamt">{rsdFormat(job.price)}</span>
                                        <span className={`pill ${job.paymentStatus === 'PAID' ? 'paid' : job.paymentStatus === 'DUE' ? 'due' : 'over'}`}>
                                            {job.paymentStatus === 'PAID' ? t('common.paid')
                                                : job.paymentStatus === 'PENDING' ? t('common.pending')
                                                : job.paymentStatus === 'DUE' ? t('orderForm.due')
                                                : (job.paymentStatus || '—')}
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Footer */}
                {pagination && (
                    <div className="hfoot">
                        <div className="rs">
                            {t('reports.showing')} <b>{((pageNumber - 1) * pageSize) + 1}–{Math.min(pageNumber * pageSize, pagination.totalItems)}</b> {t('reports.of')} <b>{pagination.totalItems}</b> {t('reports.closedJobsLabel')}
                        </div>
                        <div style={{ marginLeft: 'auto' }}>
                            <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                        </div>
                    </div>
                )}

                {reports.length > 0 && isFetching && <OverlayLoader />}
            </div>
        </div>
    );
};

export default JobReportsPage;
